import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Checkbox,
  Input,
} from "antd";
import React, { useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  HEADERS_EXPORT_DETAIL,
  POST_CONTNO_PAYMENT_DAILY,
} from "../../API/apiUrls";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";
import ThaiDatePicker from "../../../hook/ThaiDatePicker";

const { Text } = Typography;

// helper ระดับ module — ใช้ได้ทั้ง summary / export ในอนาคต
const sumRows = (rows) =>
  rows.reduce(
    (acc, r) => {
      const netpay = parseFloat(r.NETPAY) || 0;
      const isNR = r.PAY_TYPE === "NR"; // ค่างวด (มีดอกเบี้ย/เงินต้นแยก)
      const isAR = r.PAY_TYPE === "AR"; // รับอื่นๆ (ค่าทนาย/ค่าธรรมเนียม ฯลฯ)
      return {
        payamt: acc.payamt + (isNR ? parseFloat(r.PAYAMT) || 0 : 0),
        payamtN: acc.payamtN + (isNR ? parseFloat(r.PAYAMT_N) || 0 : 0),
        arOther: acc.arOther + (isAR ? parseFloat(r.PAYAMT) || 0 : 0),
        disct: acc.disct + (parseFloat(r.DISCT) || 0),
        payamtV: acc.payamtV + (parseFloat(r.PAYAMT_V) || 0),
        cashPay: acc.cashPay + (r.PAYTYP === "01" ? netpay : 0),
        transferPay: acc.transferPay + (r.PAYTYP !== "01" ? netpay : 0),
        netpay: acc.netpay + netpay,
      };
    },
    {
      payamt: 0,
      payamtN: 0,
      arOther: 0,
      disct: 0,
      payamtV: 0,
      cashPay: 0,
      transferPay: 0,
      netpay: 0,
    },
  );

const Main = () => {
  const [, , currencyFormatPoint] = CurrencyFormat();
  const roleId = localStorage.getItem("ROLE_ID");
  const showInterestInstallment = roleId === "1" || roleId === "6"; // ค่างวดรวมดอก
  const showAccountInstallment = roleId === "1" || roleId === "12"; // ค่างวดบัญชี

  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [company, setCompany] = useState(null);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [signOut] = TokenCheck();

  const [isFlagCashPay, setIsFlagCashPay] = useState(true);
  const [isFlagTransferPay, setIsFlagTransferPay] = useState(true);
  const [searchInput, setSearchInput] = useState("");

  const fmt = (val) => (val ? currencyFormatPoint(val) : "");
  const fmtPos = (val) => (parseFloat(val) > 0 ? currencyFormatPoint(val) : "");

  // ──────────────────────────────
  const onQuery = () => {
    if (!startDate || !endDate) {
      message.warning("กรุณาเลือกวันที่ก่อนค้นหา");
      return;
    }
    queryData({
      CONTNO: searchInput || null,
      firstDate: dayjs(startDate).format("YYYY-MM-DDTHH:mm:ssZ"),
      lastDate: dayjs(endDate).format("YYYY-MM-DDTHH:mm:ssZ"),
    });
  };

  const queryData = async (payload) => {
    setLoading(true);
    try {
      const res = await axios.post(POST_CONTNO_PAYMENT_DAILY, payload, {
        headers: HEADERS_EXPORT_DETAIL,
      });

      if (res.status === 200 && res.data.pay) {
        const sorted = (res.data.pay ?? [])
          .slice()
          .sort((a, b) => dayjs(a.PAYDT).valueOf() - dayjs(b.PAYDT).valueOf());
        setArrayPay(sorted);
        setCompany(res.data.company?.[0] ?? res.data.Company?.[0] ?? null);
      } else {
        message.info("ไม่พบข้อมูลการชำระ");
        setArrayPay([]);
      }
    } catch (err) {
      console.error(err);
      message.error(
        err?.response?.status > 401
          ? "ไม่พบข้อมูล"
          : "เกิดข้อผิดพลาดในการดึงข้อมูล",
      );
      setArrayPay([]);
      if (err?.response?.status === 401) {
        signOut();
      }
    } finally {
      setLoading(false);
    }
  };

  // กรองตาม checkbox เงินสด/เงินโอน
  const filteredData = useMemo(() => {
    if (!arrayPay.length) return [];
    if (isFlagCashPay && isFlagTransferPay) return arrayPay;
    if (isFlagCashPay) return arrayPay.filter((i) => i.PAYTYP === "01");
    if (isFlagTransferPay) return arrayPay.filter((i) => i.PAYTYP !== "01");
    return []; // ทั้งคู่ false → ไม่มีข้อมูล
  }, [arrayPay, isFlagCashPay, isFlagTransferPay]);

  // ──────────────────────────────
  // Columns
  // ──────────────────────────────
  const columns = [
    {
      title: "สาขา",
      dataIndex: "LOCAT",
      key: "LOCAT",
      align: "center",
      width: 60,
    },
    {
      title: "เลขที่ใบรับ",
      dataIndex: "TMBILL",
      key: "TMBILL",
      align: "center",
      width: 120,
    },
    {
      title: "วันที่รับ",
      dataIndex: "PAYDT",
      key: "PAYDT",
      align: "center",
      width: 100,
      sorter: (a, b) => dayjs(a.PAYDT).valueOf() - dayjs(b.PAYDT).valueOf(),
      render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "เลขที่สัญญา/ชื่อ",
      align: "center",
      width: 130,
      render: (_, record) => record.CONTNO,
    },
    {
      title: "ประเภท",
      dataIndex: "PAYTYP",
      key: "PAYTYP",
      align: "center",
      width: 65,
    },
    showInterestInstallment && {
      title: "ค่างวดรวมดอก",
      key: "PAYAMT",
      align: "right",
      width: 90,
      sorter: (a, b) =>
        (parseFloat(a.PAYAMT) || 0) - (parseFloat(b.PAYAMT) || 0),
      render: (_, record) =>
        record.PAY_TYPE === "NR" && record.PAYAMT ? fmt(record.PAYAMT) : 0,
    },
    showAccountInstallment && {
      title: "ค่างวดบัญชี",
      key: "PAYAMT_N",
      align: "right",
      width: 90,
      sorter: (a, b) =>
        (parseFloat(a.PAYAMT_N) || 0) - (parseFloat(b.PAYAMT_N) || 0),
      render: (_, record) =>
        record.PAY_TYPE === "NR" && record.PAYAMT_N ? fmt(record.PAYAMT_N) : 0,
    },
    { title: "ดาวน์", align: "right", width: 80, render: () => null },
    { title: "ปรับ", align: "right", width: 80, render: () => null },
    { title: "ขายสด", align: "right", width: 80, render: () => null },
    {
      title: "เงินจอง",
      align: "right",
      width: 80,
      render: (_, r) =>
        r.FLAG === "C" ? (
          <Text style={{ color: "#e74c3c", fontWeight: "bold" }}>
            **ยกเลิก**
          </Text>
        ) : null,
    },
    {
      title: "รับอื่นๆ",
      align: "right",
      width: 80,
      sorter: (a, b) =>
        (parseFloat(a.PAYAMT) || 0) - (parseFloat(b.PAYAMT) || 0),
      render: (_, record) =>
        record.PAYAMT && record.PAY_TYPE === "AR" ? fmt(record.PAYAMT) : 0,
    },
    {
      title: "ส่วนลด",
      key: "DISCT",
      align: "right",
      width: 80,
      sorter: (a, b) => (parseFloat(a.DISCT) || 0) - (parseFloat(b.DISCT) || 0),
      render: (_, record) => fmt(record.DISCT || 0),
    },
    {
      title: "ดอกเบี้ย",
      key: "PAYAMT_V",
      align: "right",
      width: 90,
      sorter: (a, b) =>
        (parseFloat(a.PAYAMT_V) || 0) - (parseFloat(b.PAYAMT_V) || 0),
      render: (_, record) => (record.PAYAMT_V ? fmt(record.PAYAMT_V) : null),
    },
    {
      title: "เงินสด",
      key: "cashPay",
      align: "right",
      width: 90,
      sorter: (a, b) =>
        (a.PAYTYP === "01" ? parseFloat(a.NETPAY) || 0 : 0) -
        (b.PAYTYP === "01" ? parseFloat(b.NETPAY) || 0 : 0),
      render: (_, record) =>
        record.NETPAY && record.PAYTYP === "01" ? fmt(record.NETPAY) : 0,
    },
    {
      title: "เงินโอน",
      key: "transferPay",
      align: "right",
      width: 90,
      sorter: (a, b) =>
        (a.PAYTYP !== "01" ? parseFloat(a.NETPAY) || 0 : 0) -
        (b.PAYTYP !== "01" ? parseFloat(b.NETPAY) || 0 : 0),
      render: (_, record) =>
        record.NETPAY && record.PAYTYP !== "01" ? fmt(record.NETPAY) : 0,
    },
    {
      title: "เงินรับฝากค่างวด",
      align: "right",
      width: 120,
      render: () => null,
    },
    showAccountInstallment && {
      title: "รับรวมสุทธิ",
      align: "right",
      width: 120,
      render: (_, record) => (record.NETPAY ? fmt(record.NETPAY) : 0),
    },
  ].filter(Boolean);

  const rowClassName = (record) => (record.FLAG === "C" ? "row-cancelled" : "");
  const hasDate = startDate && endDate;

  return (
    <>
      <style>{`
        .row-cancelled td {
          color: #e74c3c !important;
          font-weight: bold;
        }
      `}</style>

      <Spin spinning={loading} size="large" tip="Loading...">
        <Row>
          <Col span={12} style={{ textAlign: "start" }}>
            <Space style={{ marginBottom: 10 }}>
              เริ่ม
              <ThaiDatePicker
                size="large"
                value={startDate}
                onChange={setStartDate}
                placeholder="วันที่เริ่มต้น"
                allowClear={false}
              />
              ถึง
              <ThaiDatePicker
                size="large"
                value={endDate}
                onChange={setEndDate}
                placeholder="วันที่สิ้นสุด"
                allowClear={false}
              />
              <Checkbox
                checked={isFlagCashPay}
                onChange={(e) => setIsFlagCashPay(e.target.checked)}
              >
                เงินสด
              </Checkbox>
              <Checkbox
                checked={isFlagTransferPay}
                onChange={(e) => setIsFlagTransferPay(e.target.checked)}
              >
                เงินโอน
              </Checkbox>
              <Input
                placeholder="ค้นหาเลขสัญญา"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onPressEnter={onQuery}
                style={{ width: 250, borderRadius: 8 }}
                size="small"
                allowClear
                suffix={<span style={{ marginRight: 5 }}>🔍</span>}
              />
              <Button size="large" type="primary" onClick={onQuery}>
                ค้นหา
              </Button>
            </Space>
          </Col>
        </Row>

        <Card style={{ marginBottom: "10px" }}>
          <Divider style={{ fontSize: 20 }}>รายงานการรับชำระประจำวัน</Divider>

          {company && (
            <div style={{ marginBottom: 4, textAlign: "center" }}>
              <Typography.Text
                strong
                style={{ display: "block", fontSize: 16 }}
              >
                {company.COMP_NM}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                style={{ display: "block", fontSize: 16 }}
              >
                {company.COMP_ADR1}
              </Typography.Text>
            </div>
          )}

          {hasDate && (
            <Typography.Text
              style={{ display: "block", textAlign: "center", fontSize: 16 }}
            >
              จากวันที่ {dayjs(startDate).format("DD/MM/YYYY")} ถึงวันที่{" "}
              {dayjs(endDate).format("DD/MM/YYYY")}
            </Typography.Text>
          )}

          <Table
            style={{ marginTop: "10px" }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey={(record) =>
              record.wctId ?? `${record.TMBILL}-${record.CONTNO}`
            }
            rowClassName={rowClassName}
            scroll={{ x: 1400 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (total) => `ทั้งหมด ${total} รายการ`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            summary={() => {
              const cancelledRows = filteredData.filter((r) => r.FLAG === "C");
              const validRows = filteredData.filter((r) => r.FLAG !== "C");
              const totalsAll = sumRows(filteredData);
              const totalsCancelled = sumRows(cancelledRows);
              const totalsValid = sumRows(validRows);

              const SummaryRow = ({
                label,
                count,
                t,
                style = {},
                cellStyle = {},
              }) => (
                <Table.Summary.Row
                  style={{
                    background: "#ecf0f1",
                    fontWeight: "bold",
                    ...style,
                  }}
                >
                  {/* สาขา → ประเภท = 5 columns */}
                  <Table.Summary.Cell
                    colSpan={5}
                    align="right"
                    style={cellStyle}
                  >
                    {label} {count} รายการ จำนวนเงิน :
                  </Table.Summary.Cell>

                  {/* ค่างวดรวมดอก — role 1/6 */}
                  {showInterestInstallment && (
                    <Table.Summary.Cell align="right" style={cellStyle}>
                      {t.payamt ? fmtPos(t.payamt) : null}
                    </Table.Summary.Cell>
                  )}

                  {/* ค่างวดบัญชี — role 1/12 */}
                  {showAccountInstallment && (
                    <Table.Summary.Cell align="right" style={cellStyle}>
                      {t.payamtN ? fmtPos(t.payamtN) : null}
                    </Table.Summary.Cell>
                  )}

                  {/* ดาวน์, ปรับ, ขายสด, เงินจอง — empty */}
                  <Table.Summary.Cell />
                  <Table.Summary.Cell />
                  <Table.Summary.Cell />
                  <Table.Summary.Cell />

                  {/* รับอื่นๆ */}
                  <Table.Summary.Cell align="right" style={cellStyle}>
                    {t.arOther ? fmtPos(t.arOther) : null}
                  </Table.Summary.Cell>

                  {/* ส่วนลด */}
                  <Table.Summary.Cell align="right" style={cellStyle}>
                    {t.disct ? fmtPos(t.disct) : null}
                  </Table.Summary.Cell>

                  {/* ดอกเบี้ย */}
                  <Table.Summary.Cell align="right" style={cellStyle}>
                    {t.payamtV ? fmtPos(t.payamtV) : null}
                  </Table.Summary.Cell>

                  {/* เงินสด */}
                  <Table.Summary.Cell align="right" style={cellStyle}>
                    {t.cashPay ? fmtPos(t.cashPay) : null}
                  </Table.Summary.Cell>

                  {/* เงินโอน */}
                  <Table.Summary.Cell align="right" style={cellStyle}>
                    {t.transferPay ? fmtPos(t.transferPay) : null}
                  </Table.Summary.Cell>

                  {/* เงินรับฝากค่างวด — empty */}
                  <Table.Summary.Cell />

                  {/* รับรวมสุทธิ — role 1/12 */}
                  {showAccountInstallment && (
                    <Table.Summary.Cell align="right" style={cellStyle}>
                      {t.netpay ? fmtPos(t.netpay) : null}
                    </Table.Summary.Cell>
                  )}
                </Table.Summary.Row>
              );

              return (
                <>
                  <SummaryRow
                    label="รวมทั้งหมด"
                    count={filteredData.length}
                    t={totalsAll}
                  />
                  <SummaryRow
                    label="รวมยกเลิกทั้งหมด"
                    count={cancelledRows.length}
                    t={totalsCancelled}
                    style={{ background: "#ffebeb" }}
                    cellStyle={{ color: "#dc3545" }}
                  />
                  <SummaryRow
                    label="รวมรับชำระจริง"
                    count={validRows.length}
                    t={totalsValid}
                    style={{ background: "#d4edda" }}
                    cellStyle={{ color: "#155724" }}
                  />
                </>
              );
            }}
          />
        </Card>
      </Spin>
    </>
  );
};

const MainContnoReceivePayment = MotionHoc(Main);
export default MainContnoReceivePayment;
